import React, { useState, useEffect } from 'react';
import axios from 'axios';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3001';

const SchedulePreview = ({ draft, onClose, onApprove, onReject }) => {
  const [currentSessions, setCurrentSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [viewMode, setViewMode] = useState('comparison'); // comparison, new, current

  const timeSlots = ['8h30-10h00', '10h10-11h40', '11h50-13h20', '13h30-15h00', '15h10-16h40', '16h50-18h20'];
  const days = ['Lundi', 'Mardi', 'Mercredi', 'Jeudi', 'Vendredi', 'Samedi'];

  useEffect(() => {
    fetchCurrentSessions();
  }, [draft]);

  const fetchCurrentSessions = async () => {
    try {
      const classIds = [...new Set(draft.sessions.map(s => s.class._id || s.class))];
      const allSessions = [];

      for (const classId of classIds) {
        const response = await axios.get(`${API_URL}/api/sessions?class=${classId}&semester=${draft.semester}&limit=10000&_t=${Date.now()}`);
        const sessionsData = Array.isArray(response.data) ? response.data : response.data.data || [];
        allSessions.push(...sessionsData);
      }

      setCurrentSessions(allSessions);
    } catch (error) {
      console.error('Erreur chargement sessions actuelles:', error);
    } finally {
      setLoading(false);
    }
  };

  const getSessionsForSlot = (sessions, classId, dayIndex, timeSlotIndex) => {
    return sessions.filter(session => {
      const sessionClassId = session.class?._id || session.class;
      return sessionClassId === classId &&
             session.dayOfWeek === dayIndex + 1 &&
             session.timeSlot === timeSlotIndex + 1;
    });
  };

  const getUniqueClasses = () => {
    const classIds = new Set();
    const classMap = new Map();

    // Ajouter classes du brouillon
    draft.sessions.forEach(session => {
      const classId = session.class?._id || session.class;
      classIds.add(classId);
      if (session.class?.name) {
        classMap.set(classId, session.class);
      }
    });

    // Ajouter classes actuelles
    currentSessions.forEach(session => {
      const classId = session.class?._id || session.class;
      classIds.add(classId);
      if (session.class?.name) {
        classMap.set(classId, session.class);
      }
    });

    return Array.from(classIds).map(id => classMap.get(id) || { _id: id, name: `Classe ${id}` });
  };

  const renderSessionCell = (sessions, isNew = false) => {
    if (!sessions || sessions.length === 0) {
      return <div className="h-10 border border-gray-200 rounded bg-gray-50"></div>;
    }

    return (
      <div className={`h-10 border rounded p-0.5 ${isNew ? 'border-green-300 bg-green-50' : 'border-gray-200 bg-white'}`}>
        {sessions.map((session, index) => (
          <div key={index} className="leading-tight">
            <div className="font-medium truncate text-xs">
              {session.course?.code || session.course?.name || 'Cours'}
            </div>
            <div className="text-gray-600 truncate" style={{fontSize: '10px'}}>
              {session.teacher?.firstName?.charAt(0)}.{session.teacher?.lastName || 'Ens'}
            </div>
          </div>
        ))}
      </div>
    );
  };

  const renderComparisonView = () => {
    const classes = getUniqueClasses();

    return (
      <div className="space-y-6">
        {classes.map(cls => (
          <div key={cls._id} className="bg-white rounded-lg border">
            <div className="p-3 border-b bg-gray-50">
              <h4 className="font-medium">{cls.name}</h4>
            </div>
            
            <div className="grid grid-cols-2 gap-4 p-4">
              {/* Emploi du temps actuel */}
              <div>
                <h5 className="text-sm font-medium text-gray-700 mb-2">📅 Actuel</h5>
                <div className="overflow-x-auto">
                  <table className="w-full text-xs">
                    <thead>
                      <tr>
                        <th className="text-left p-1 w-16">Horaire</th>
                        {days.slice(0, 5).map((day, index) => (
                          <th key={index} className="text-center p-1 min-w-20">{day.slice(0, 3)}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {timeSlots.map((slot, slotIndex) => (
                        <tr key={slotIndex}>
                          <td className="p-1 text-xs font-medium">{slot}</td>
                          {days.slice(0, 5).map((day, dayIndex) => (
                            <td key={dayIndex} className="p-0.5">
                              {renderSessionCell(getSessionsForSlot(currentSessions, cls._id, dayIndex, slotIndex))}
                            </td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Nouvel emploi du temps */}
              <div>
                <h5 className="text-sm font-medium text-green-700 mb-2">🤖 Nouveau (IA)</h5>
                <div className="overflow-x-auto">
                  <table className="w-full text-xs">
                    <thead>
                      <tr>
                        <th className="text-left p-1 w-16">Horaire</th>
                        {days.slice(0, 5).map((day, index) => (
                          <th key={index} className="text-center p-1 min-w-20">{day.slice(0, 3)}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {timeSlots.map((slot, slotIndex) => (
                        <tr key={slotIndex}>
                          <td className="p-1 text-xs font-medium">{slot}</td>
                          {days.slice(0, 5).map((day, dayIndex) => (
                            <td key={dayIndex} className="p-0.5">
                              {renderSessionCell(getSessionsForSlot(draft.sessions, cls._id, dayIndex, slotIndex), true)}
                            </td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>
    );
  };

  const renderSingleView = (sessions, title, bgColor = 'bg-white') => {
    const classes = getUniqueClasses();

    return (
      <div className="space-y-4">
        <h4 className="text-lg font-medium">{title}</h4>
        {classes.map(cls => (
          <div key={cls._id} className={`rounded-lg border ${bgColor}`}>
            <div className="p-3 border-b bg-gray-50">
              <h5 className="font-medium">{cls.name}</h5>
            </div>
            
            <div className="p-4">
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr>
                      <th className="text-left p-2 w-24">Horaire</th>
                      {days.map((day, index) => (
                        <th key={index} className="text-center p-2 min-w-32">{day}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {timeSlots.map((slot, slotIndex) => (
                      <tr key={slotIndex}>
                        <td className="p-2 font-medium text-gray-600">{slot}</td>
                        {days.map((day, dayIndex) => (
                          <td key={dayIndex} className="p-1">
                            {renderSessionCell(getSessionsForSlot(sessions, cls._id, dayIndex, slotIndex))}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        ))}
      </div>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto">
      {/* En-tête avec onglets */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex gap-2">
          <button
            onClick={() => setViewMode('comparison')}
            className={`px-4 py-2 text-sm font-medium rounded-lg ${
              viewMode === 'comparison' 
                ? 'bg-primary-100 text-primary-800' 
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            📊 Comparaison
          </button>
          <button
            onClick={() => setViewMode('new')}
            className={`px-4 py-2 text-sm font-medium rounded-lg ${
              viewMode === 'new' 
                ? 'bg-green-100 text-green-800' 
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            🤖 Nouveau
          </button>
          <button
            onClick={() => setViewMode('current')}
            className={`px-4 py-2 text-sm font-medium rounded-lg ${
              viewMode === 'current' 
                ? 'bg-blue-100 text-blue-800' 
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            📅 Actuel
          </button>
        </div>

        <div className="flex gap-2">
          <button
            onClick={onReject}
            className="px-4 py-2 text-sm font-medium text-red-700 bg-red-50 rounded-lg hover:bg-red-100"
          >
            ❌ Rejeter
          </button>
          <button
            onClick={onApprove}
            className="px-4 py-2 text-sm font-medium text-green-700 bg-green-50 rounded-lg hover:bg-green-100"
          >
            ✅ Approuver
          </button>
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
          >
            Fermer
          </button>
        </div>
      </div>

      {/* Métriques */}
      <div className="grid grid-cols-4 gap-4 mb-6">
        <div className="bg-blue-50 p-4 rounded-lg">
          <div className="text-sm text-blue-600">Score IA</div>
          <div className="text-2xl font-bold text-blue-800">{Math.round(draft.aiScore || 0)}%</div>
        </div>
        <div className="bg-green-50 p-4 rounded-lg">
          <div className="text-sm text-green-600">Séances</div>
          <div className="text-2xl font-bold text-green-800">{draft.sessions?.length || 0}</div>
        </div>
        <div className="bg-yellow-50 p-4 rounded-lg">
          <div className="text-sm text-yellow-600">Conflits</div>
          <div className="text-2xl font-bold text-yellow-800">
            {(draft.validationResults?.teacherConflicts?.length || 0) + 
             (draft.validationResults?.roomConflicts?.length || 0)}
          </div>
        </div>
        <div className="bg-purple-50 p-4 rounded-lg">
          <div className="text-sm text-purple-600">Classes</div>
          <div className="text-2xl font-bold text-purple-800">{getUniqueClasses().length}</div>
        </div>
      </div>

      {/* Contenu selon le mode */}
      {viewMode === 'comparison' && renderComparisonView()}
      {viewMode === 'new' && renderSingleView(draft.sessions, '🤖 Nouvel emploi du temps (IA)', 'bg-green-50')}
      {viewMode === 'current' && renderSingleView(currentSessions, '📅 Emploi du temps actuel', 'bg-blue-50')}
    </div>
  );
};

export default SchedulePreview;